import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { useQuery } from '@apollo/client/react';
import { datadogRum } from '@datadog/browser-rum';
import { useAuth } from '@teamexos/fit-shared';
import React, { lazy, Suspense, useEffect } from 'react';
import {
  createBrowserRouter,
  createMemoryRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useMatches,
} from 'react-router';
import { ChatController } from '../components/Chat/ChatController';
import Classes from '../components/Classes';
import { FloatingAlerts } from '../components/FloatingAlerts';
import { GameplanTemplates } from '../components/MemberProfile/MemberGameplans/GameplanTemplates/GameplanTemplates';
import { MemberProfileLayout } from '../components/MemberProfile/MemberProfileLayout';
import { CustomizeTrainingPlanTemplate } from '../components/MemberProfile/TrainingPlans/CustomizeTrainingPlanTemplate';
import { TrainingPlanTemplates } from '../components/MemberProfile/TrainingPlans/TrainingPlanTemplates';
import PrivateRoute from '../components/PrivateRoute';
import { LoadingPage } from '../components/reusable/LoadingPage';
import { SnackbarVariant } from '../components/reusable/Snackbar';
import { useSnackbarContext } from '../components/reusable/Snackbar/SnackbarContextProvider';
import SignIn from '../components/SignIn';
import { CoachType, SelfDocument } from '../graphql/types';
import useLogout from '../hooks/useLogout';
import { useMemberName } from '../hooks/useMemberName';
import { mixpanelClient } from '../utils/mixpanelClient';
import { ErrorBoundary } from './ErrorBoundary';
import Playground from './playground';

const GamePlanEditor = lazy(() =>
  import('../components/MemberProfile/MemberGameplans/GameplanEditor').then(
    (module) => ({ default: module.GamePlanEditor }),
  ),
);

const CoachSettings = lazy(() =>
  import('../components/CoachSettings').then((module) => ({
    default: module.CoachSettings,
  })),
);

const MemberGate = lazy(() => import('../components/MemberGate'));
const HomePage = lazy(() => import('../components/HomePage'));
const Member = lazy(() => import('../components/MemberProfile'));
const Members = lazy(() => import('../components/Members'));
const Sessions = lazy(() => import('../components/Sessions'));
const Services = lazy(() => import('../components/Services'));
const Methodology = lazy(() => import('../components/Methodology'));
const MovementLibrary = lazy(() => import('../components/MovementLibrary'));

const PageSuspense: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Suspense fallback={<LoadingPage />}>{children}</Suspense>
);

const MainScreen: React.FC = () => {
  const { data, loading } = useQuery(SelfDocument);

  const coachType = data?.self?.coachData?.type;

  if (loading) {
    return null;
  }

  if (coachType === CoachType.CalendarOnly) {
    return <Navigate to="/app/classes" replace />;
  }

  if (coachType === CoachType.Coach || coachType === CoachType.Dietitian) {
    return <HomePage />;
  }

  return <MemberGate />;
};

const AppWrapper = () => {
  const { isLoggedIn, isLoading: isAuthLoading, error: authError } = useAuth();
  const { setSnackbar } = useSnackbarContext();
  const logout = useLogout();
  const { data } = useQuery(SelfDocument, {
    skip: !isLoggedIn || isAuthLoading,
  });

  useEffect(() => {
    if (authError) {
      type AuthGraphQLError = {
        message?: string;
        extensions?: Record<string, string>;
      };
      let authGraphQLErrors: AuthGraphQLError[] = [];
      if (CombinedGraphQLErrors.is(authError)) {
        authGraphQLErrors = authError.errors as AuthGraphQLError[];
      } else if (ServerError.is(authError)) {
        try {
          authGraphQLErrors = JSON.parse(authError.bodyText).errors ?? [];
        } catch {
          authGraphQLErrors = [];
        }
      }
      const isUnauthenticated = authGraphQLErrors.some(
        (error) =>
          error.message?.includes('User inactive') ||
          error.extensions?.code === 'UNAUTHENTICATED',
      );
      if (isUnauthenticated) {
        const error = new Error('User inactive/unauthenticated, logging out');
        datadogRum.addError(error, {
          error: authError,
        });
        setSnackbar({
          message:
            'Authentication error. Contact support if this issue persists.',
          variant: SnackbarVariant.Danger,
        });
        logout();
        return;
      }
    }

    if (!data?.self?.id) {
      return;
    }

    datadogRum.setUser({
      id: data.self.id,
    });
    mixpanelClient.identify(data.self.id);
  }, [data?.self?.id, authError, logout, setSnackbar]);

  return (
    <>
      {isLoggedIn && <FloatingAlerts />}
      <Outlet />
      {isLoggedIn && <ChatController />}
    </>
  );
};

interface RouteHandle {
  title?: string;
}

/**
 * Sets the browser tab title from the active route's `handle.title`. When the
 * route is scoped to a member (`:memberId`), the member's name is appended
 * (e.g. "Gameplan · John D."); for the member profile itself the name is used
 * on its own.
 */
export const DocumentTitle = () => {
  const matches = useMatches();
  const BASE_TITLE = 'Coach Hub';

  const title = [...matches]
    .reverse()
    .map((match) => (match.handle as RouteHandle | undefined)?.title)
    .find(Boolean);

  const memberId = matches.find(
    (match) => (match.params as { memberId?: string }).memberId,
  )?.params.memberId;
  const memberName = useMemberName(memberId, { lastNameInitial: true });

  const combinedTitle = [title, memberName, BASE_TITLE]
    .filter(Boolean)
    .join(' · ');

  return <title>{combinedTitle}</title>;
};

const RootLayout = () => (
  <>
    <DocumentTitle />
    <Outlet />
  </>
);

const routesConfig = createRoutesFromElements(
  <Route path="/" element={<RootLayout />} ErrorBoundary={ErrorBoundary}>
    {/* Redirect from / to /app */}
    <Route index element={<Navigate to="/app" replace />} />
    <Route path="app">
      <Route path="login" handle={{ title: 'Sign In' }} element={<SignIn />} />
      <Route
        path="playground"
        handle={{ title: 'Playground' }}
        element={
          process.env.GATSBY_ENV_LEVEL === 'dev' ? (
            <Playground />
          ) : (
            <Navigate to="/app" replace />
          )
        }
      />
      <Route element={<AppWrapper />}>
        <Route
          index
          handle={{ title: 'Home' }}
          element={
            <PrivateRoute>
              <PageSuspense>
                <MainScreen />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="fitMembersList"
          path="members"
          handle={{ title: 'Members' }}
          element={
            <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
              <PageSuspense>
                <Members />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          path="members/:memberId"
          element={
            <PrivateRoute>
              <MemberProfileLayout />
            </PrivateRoute>
          }
        >
          <Route
            key="membersProfile"
            index
            element={
              <PrivateRoute
                allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
              >
                <PageSuspense>
                  <Member />
                </PageSuspense>
              </PrivateRoute>
            }
          />
          <Route
            key="gameplanCreator"
            path="gameplan"
            handle={{ title: 'Create Gameplan' }}
            element={
              <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
                <PageSuspense>
                  <GamePlanEditor state="create" />
                </PageSuspense>
              </PrivateRoute>
            }
          />
          <Route
            key="gameplanViewer"
            path="gameplan/:planId"
            handle={{ title: 'Gameplan' }}
            element={
              <PrivateRoute
                allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
              >
                <PageSuspense>
                  <GamePlanEditor state="view" />
                </PageSuspense>
              </PrivateRoute>
            }
          />
          <Route
            key="gameplanEditor"
            path="gameplan/:planId/edit"
            handle={{ title: 'Edit Gameplan' }}
            element={
              <PrivateRoute
                allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
              >
                <PageSuspense>
                  <GamePlanEditor state="edit" />
                </PageSuspense>
              </PrivateRoute>
            }
          />
          <Route
            key="gameplanTemplates"
            path="gameplanTemplates"
            handle={{ title: 'Gameplan Templates' }}
            element={
              <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
                <PageSuspense>
                  <GameplanTemplates />
                </PageSuspense>
              </PrivateRoute>
            }
          />
          <Route
            key="trainingPlanTemplates"
            path="trainingPlan"
            handle={{ title: 'Training Plan Templates' }}
            element={
              <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
                <PageSuspense>
                  <TrainingPlanTemplates />
                </PageSuspense>
              </PrivateRoute>
            }
          />
          <Route
            key="trainingPlan"
            path="trainingPlan/:planId/"
            handle={{ title: 'Training Plan' }}
            element={
              <PrivateRoute
                allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
              >
                <PageSuspense>
                  <CustomizeTrainingPlanTemplate />
                </PageSuspense>
              </PrivateRoute>
            }
          />
        </Route>
        <Route
          key="sessions"
          path="sessions"
          handle={{ title: 'Sessions' }}
          element={
            <PrivateRoute
              allowedCoachTypes={[CoachType.Coach, CoachType.Dietitian]}
            >
              <PageSuspense>
                <Sessions />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="services"
          path="services"
          handle={{ title: 'Services' }}
          element={
            <PrivateRoute allowedCoachTypes={[CoachType.Coach]}>
              <PageSuspense>
                <Services />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="classes"
          path="classes"
          handle={{ title: 'Classes' }}
          element={
            <PrivateRoute
              allowedCoachTypes={[
                CoachType.Coach,
                CoachType.Dietitian,
                CoachType.CalendarOnly,
              ]}
            >
              <PageSuspense>
                <Classes />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="movementLibrary"
          path="movement-library"
          handle={{ title: 'Movement Library' }}
          element={
            <PrivateRoute
              allowedCoachTypes={[
                CoachType.Coach,
                CoachType.Dietitian,
                CoachType.CalendarOnly,
              ]}
            >
              <PageSuspense>
                <MovementLibrary />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="methodology"
          path="methodology"
          handle={{ title: 'Methodology' }}
          element={
            <PrivateRoute
              allowedCoachTypes={[
                CoachType.Coach,
                CoachType.Dietitian,
                CoachType.CalendarOnly,
              ]}
            >
              <PageSuspense>
                <Methodology />
              </PageSuspense>
            </PrivateRoute>
          }
        />
        <Route
          key="coachSettings"
          path="settings/:settingsPage"
          handle={{ title: 'Settings' }}
          element={
            <PrivateRoute
              allowedCoachTypes={[
                CoachType.Coach,
                CoachType.Dietitian,
                CoachType.CalendarOnly,
              ]}
            >
              <PageSuspense>
                <CoachSettings />
              </PageSuspense>
            </PrivateRoute>
          }
        />
      </Route>
    </Route>
    <Route
      path="settings"
      element={<Navigate to="/app/settings/availability" replace />}
    />
    {/* Redirect from /anything to /app - this is required in addition to the redirect above */}
    <Route path="*" element={<Navigate to="/app" replace />} />
  </Route>,
);

const browserRouter = createBrowserRouter(routesConfig);
export const BrowserRouter = () => <RouterProvider router={browserRouter} />;

// Browser router doesn't work in jest tests and we can't export non-react components here
// so we need to create a memory router for tests https://github.com/vitejs/vite/discussions/4583
const memoryRouter = (initialEntries: string[]) =>
  createMemoryRouter(routesConfig, {
    initialEntries,
  });
export const TestRouter = ({
  initialEntries,
}: {
  initialEntries: string[];
}) => <RouterProvider router={memoryRouter(initialEntries)} />;
